import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { PDFDocument, degrees } from 'pdf-lib';

// Set worker source
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

/**
 * Generates thumbnails for all pages in a PDF file
 * @param {File} file - The PDF file
 * @param {number} maxWidth - Maximum width for the thumbnail
 * @returns {Promise<Array>} Array of page objects with thumbnail data URLs
 */
export const getAllPageThumbnails = async (file, maxWidth = 200) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument(arrayBuffer).promise;
    const pageCount = pdf.numPages;
    const thumbnails = [];

    for (let i = 1; i <= pageCount; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 1.0 });
        const scale = maxWidth / viewport.width;
        const scaledViewport = page.getViewport({ scale });

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = scaledViewport.height;
        canvas.width = scaledViewport.width;

        await page.render({ canvasContext: context, viewport: scaledViewport }).promise;

        thumbnails.push({
            id: crypto.randomUUID(), // Unique ID for React keys and drag-drop
            originalIndex: i - 1, // 0-based index for pdf-lib
            pageNumber: i,
            thumbnail: canvas.toDataURL('image/jpeg', 0.7),
            rotation: 0, // Visual rotation state
        });
    }

    return thumbnails;
};

/**
 * Creates a new PDF based on the provided page configuration
 * @param {File} originalFile - The original PDF file
 * @param {Array} pagesConfig - Array of page objects { originalIndex, rotation }
 * @returns {Promise<Blob>} The new PDF blob
 */
export const saveReorderedPdf = async (originalFile, pagesConfig) => {
    const arrayBuffer = await originalFile.arrayBuffer();
    const existingPdfDoc = await PDFDocument.load(arrayBuffer);
    const newPdfDoc = await PDFDocument.create();

    // We need to copy pages. 
    // Note: copyPages can take an array of indices. 
    // However, since we might have duplicates and individual rotations, 
    // it's safer to copy them one by one or in batches if distinct.
    // Given we might have the same page multiple times (duplication), 
    // we simply iterate through our config.

    for (const pageConfig of pagesConfig) {
        // Copy the specific page
        const [copiedPage] = await newPdfDoc.copyPages(existingPdfDoc, [pageConfig.originalIndex]);

        // Apply rotation if needed
        // Existing rotation is already on the page, we add our visual rotation to it
        const currentRotation = copiedPage.getRotation().angle;
        copiedPage.setRotation(degrees(currentRotation + pageConfig.rotation));

        newPdfDoc.addPage(copiedPage);
    }

    const pdfBytes = await newPdfDoc.save();
    return new Blob([pdfBytes], { type: 'application/pdf' });
};
